import React from "react";

import styles from "./singleAssetAgentDev.module.css";
import { Message } from "./AgentMessage";
import { TurnDiagnostics } from "./TurnDiagnostics";
import { AGENT_PANEL_ENABLED } from "../../agentPanel";
import { AssetTagEditor } from "../asset-overview/AssetTagEditor";
import {
  assetTitle,
  assetTypeLabel,
  display,
  nowLabel,
} from "./agentMessageFormat";

export function accountingViewFromBinding(view) {
  if (!view || !view.target || !view.calculation) return null;
  const calculation = view.calculation ?? {};
  return {
    ...calculation,
    status: calculation.status ?? (calculation.calculation_result ? "ready" : "needs_input"),
    rows: view.facts?.transactions ?? [],
    // 基线（期初持仓）和流水是两类事实，都要带上：只带流水的话，
    // 「只有基线、没有流水」的持仓在下面会被判成「事实被删空」。
    opening_position: view.facts?.opening_position ?? calculation.opening_position ?? null,
    active_draft: {
      draft_id: null,
      revision: view.target.revision,
      asset_code: view.target.asset_code,
      asset_name: view.target.asset_name,
      asset_type: view.target.asset_type,
    },
    binding: view.binding,
    target: view.target,
  };
}

export function AgentChatPanel({
  activityLabel, busy, embedded, fileImportError, fileImporting,
  input, interactionBusy, messages, onInput,
  onOpenFile, onResize, onSubmit,
  trackedMetrics, displayRows, threadRef,
  composerInputRef, turnResult,
}) {
  return (
    <aside className={`${styles.chatPanel} ${!embedded ? "resizable-agent-panel" : ""}`} aria-label="投资理财助手 对话">
      {!embedded && (
        <div className={`agent-resize-handle ${styles.resizeHandle}`} role="separator" aria-orientation="vertical" aria-label="拖动调整 Agent 宽度" onMouseDown={onResize} />
      )}
      <div className={styles.chatTitle}>
        <span>投资理财助手</span>
      </div>
      <div className={styles.chatThread} ref={threadRef} aria-live="polite">
        {messages.map((item, index) => <Message key={`${item.time}-${index}`} item={item} />)}
        <TurnDiagnostics turnResult={turnResult} rendered={{
          source: turnResult?.market_view ? "market_view" : "binding/兜底",
          assetCode: turnResult?.market_view?.asset_code ?? displayRows[0]?.asset_code,
          quantity: trackedMetrics?.quantity,
          costPrice: trackedMetrics?.cost_per_share,
        }} />
        {busy && (
          <article className={`${styles.chatMessage} ${styles.agentMessage}`}>
            <div className={styles.chatAvatar}>AI</div>
            <div className={styles.messageBody}><time>{nowLabel()}</time><div className={`${styles.agentBubble} ${styles.thinkingBubble}`}>
              <span /><span /><span /><small>{activityLabel}</small>
            </div></div>
          </article>
        )}
      </div>
      <form className={styles.composer} onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
        <button className={styles.agentButton} type="button" aria-label="向 Agent 导入文件" title="导入 XLSX / CSV / TXT" onClick={onOpenFile} disabled={interactionBusy}>{fileImporting ? "…" : "＋"}</button>
        <input ref={composerInputRef} value={input} onChange={(event) => onInput(event.target.value)} aria-label="补充或修改交易信息" />
        <button className={styles.sendButton} type="submit" aria-label="发送给 Agent" disabled={!input.trim() || interactionBusy}>→</button>
      </form>
      {fileImportError && <small className={styles.composerError} role="alert">{fileImportError}</small>}
    </aside>
  );
}

export function WorkspaceHeader({
  calculationResult, hasResult, onRenderAction, rows, status,
  systemDataBlocked, turnResult, onEditHolding = null,
}) {
  // 事实已保存、核算是系统原因失败（不是用户能改的门禁问题）：明说，别让人以为是自己没填完
  // （transaction-dialog-ledger-edit AC-011）。
  const failure = turnResult?.calculation_failure;
  const systemFailure = !hasResult && turnResult?.calculation_status === "failed"
    && Boolean(failure) && !failure.user_correctable;
  return (
    <header className={styles.workspaceHead}>
      <div>
        <div className={styles.titleLine}>
          <span className={`${styles.agentBadge} ${styles.assetBadge}`}>{assetTypeLabel(rows[0])}</span>
          <h1>{assetTitle(rows)}</h1>
          {turnResult?.active_draft?.asset_code && <AssetTagEditor assetCode={turnResult.active_draft.asset_code} compact />}
          {systemFailure && <span className={`${styles.agentBadge} ${styles.blockedBadge}`}>核算失败</span>}
          {status === "needs_input" && !systemFailure && <span className={`${styles.agentBadge} ${systemDataBlocked ? styles.warningBadge : styles.blockedBadge}`}>{systemDataBlocked ? "行情待补齐" : "输入待补充"}</span>}
          {status === "needs_confirmation" && <span className={`${styles.agentBadge} ${styles.warningBadge}`}>价格待确认</span>}
          {hasResult && <span className={`${styles.agentBadge} ${styles.validBadge}`}>正式结果</span>}
        </div>
        {systemFailure && (
          <p className={styles.metadata} role="status">
            记录已保存，核算失败：{failure.reason || "请稍后重新打开这只标的重试。"}
          </p>
        )}
        <p className={styles.metadata}>
          {hasResult
            ? `${rows.length} 笔交易 · 首笔交易日 ${display([...rows].sort((a, b) => String(a.trade_date).localeCompare(String(b.trade_date)))[0]?.trade_date)} · 核算截止日 ${display(calculationResult.valuation_date)}`
            : `${rows.length} 笔交易${AGENT_PANEL_ENABLED ? " · 所有操作均通过左侧 Agent 对话完成" : ""}${turnResult?.llm_steps ? ` · ${turnResult.llm_steps} 次模型调用 · ${(turnResult.client_elapsed_ms / 1000).toFixed(1)} 秒` : ""}`}
        </p>
      </div>
      {hasResult && onRenderAction()}
      {/* 核算被门禁挡住时右侧只剩这个头部——「记录交易」按钮长在结果卡里，
          结果卡不渲染，用户就再也打不开那个弹窗，事实明明还在库里却改不动了
          （2026-09-03 用户实测，AGENT-096）。这里补一个同样的入口。 */}
      {!hasResult && rows.length > 0 && onEditHolding && (
        <button type="button" className={styles.headerEditButton} onClick={onEditHolding}>
          记录交易
        </button>
      )}
    </header>
  );
}
